import { Button, Space, Table, notification, Tooltip, Form, Popconfirm, Input, Row, Col, Avatar } from "antd";
import style from "./index.module.scss";
import classNames from "classnames/bind";
import { useState } from "react";
import ModalCreateShop from "./ModalCreateShop";
import { getUser } from "utils/index";
import { EditOutlined, PlusOutlined, CloudUploadOutlined, DeleteOutlined, HistoryOutlined, StarFilled } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { rolesActions, rolesAdd, rolesExport } from "../../../roles";
import ModalImport from "../ModalImport";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import mapApi from "api/map";
import Table2XLSX from "./TableExport";
import useImportJob from "hooks/useImportJob";
import ImportProgressCard from "components/ImportProgressCard";
import ImportHistory from "components/ImportHistory";
import { JOB_PROCESSING } from "constants/index";
import moment from "moment";

const cx = classNames.bind(style);

export default function ShopMapList({ getAllShop, listShop, loading, setLoading }) {
  const [formImport] = Form.useForm();
  const [formCU] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const { startJobPolling, clearJob } = useImportJob();

  const { t } = useTranslation()

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({ message, description });
  };

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState();
  const [openImport, setOpenImport] = useState(false);
  const [currentImportJob, setCurrentImportJob] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [filters, setFilters] = useState({
    code: '',
    gl: '',
    address: ''
  });

  // Filter logic
  const filteredListShop = listShop?.filter(item => {
    return (
      item.code?.toLowerCase().includes(filters.code.toLowerCase()) &&
      item.gl?.toLowerCase().includes(filters.gl.toLowerCase()) &&
      item.address?.toLowerCase().includes(filters.address.toLowerCase())
    );
  }) || [];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get unique GL values from listShop
  const uniqueGLOptions = [...new Set(
    listShop
      ?.map(shop => shop.gl)
      .filter(gl => gl && gl.trim() !== '')
  )] || [];

  const columns = [
    {
      title: t('shopCode'),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t('shopName'),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t('address'),
      dataIndex: "address",
      key: "address",
      render: (_) => (
        <Tooltip title={_}>
          <span className={cx("ellipsis")}>{_}</span>
        </Tooltip>
      ),
    },
    {
      title: t('timeOpened'),
      dataIndex: "openedAt",
      key: "openedAt",
    },
    {
      title: t('departmentManager'),
      dataIndex: "gl",
      key: "gl",
    },
  ];

  if (rolesActions.includes(+getUser().role)) {
    columns.push({
      title: t('action'),
      dataIndex: "action",
      key: "aciton",
      render: (text, row) => {
        return (
          <Space>
            <Button
              title={t('edit')}
              onClick={() => {
                setOpenModal(true);
                setSelected(row);
              }}
            >
              <Tooltip title={t('edit')}>
                <EditOutlined />
              </Tooltip>
            </Button>
            <Popconfirm
              title={t('sureToDeleteShop')}
              description=""
              onConfirm={() => onConfirmDelete(row._id)}
            >
              <Button danger>
                <Tooltip title={t('delete')}>
                  <DeleteOutlined />
                </Tooltip>
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    });
  }

  const onConfirmDelete = async (id) => {
    await mapApi.deleteShop(id);
    openNotificationWithIcon("success", t('notification'), t("deleteShopSuccess"));
    getAllShop();
  };

  const onCancel = () => {
    setOpenModal(false);
    setSelected(null);
    formCU.resetFields();
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      if (!selected) {
        const res = await mapApi.createShop({
          ...values,
          openedAt: values.openedAt ? moment(values.openedAt).format('MM/YYYY') : null,
        });
        if (res?.success) {
          onCancel();
          openNotificationWithIcon("success", t('notification'), t('createShopSuccess'));
          getAllShop();
        }
      } else {
        const res = await mapApi.updateShop(selected._id, {
          ...values,
          openedAt: values.openedAt ? moment(values.openedAt).format('MM/YYYY') : null,
        });
        if (res?.success) {
          onCancel();
          openNotificationWithIcon("success", t('notification'), t("updateShopSuccess"));
          getAllShop();
        }
      }
    } catch (error) {
      openNotificationWithIcon("error", t('error'), error?.response?.data?.message || t('hasError'));
    } finally {
      setLoading(false);
    }
  };

  const onImport = async (values) => {
    try {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;
      reader.onload = async (e) => {
        try {
          /* Parse data */
          const bstr = e.target.result;
          const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
          /* Get first worksheet */
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          /* Convert array of arrays */
          const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

          if (Array.isArray(data) && data.length >= 2) {
            let payload = []
            let error = ''
            data.filter(item => item.length > 0 && item).forEach((item, idx) => {
              if (idx > 0) {
                const [
                  code,
                  name,
                  address,
                  gl,
                  openedAt
                ] = item

                payload.push({
                  code,
                  name,
                  address,
                  gl,
                  openedAt,
                })
              }
            })

            if (!error && payload.length > 0) {
              try {
                // Create async import job
                const { jobId } = await mapApi.createShopImportJob(payload);

                closeModalImport();
                openNotificationWithIcon("success", t('notification'), t('importJobStarted'));

                // Start polling for job status
                startJobPolling(
                  jobId,
                  (jobData) => {
                    // Update job progress
                    setCurrentImportJob(jobData);
                  },
                  (jobData) => {
                    // Job completed successfully
                    openNotificationWithIcon("success", t('notification'), t('importSuccess'));
                    getAllShop();
                    setCurrentImportJob(null);
                  },
                  (jobData) => {
                    // Job failed
                    openNotificationWithIcon("error", t('error'), jobData.error || t('importFailed'));
                    setCurrentImportJob(jobData);
                  }
                );

              } catch (error) {
                openNotificationWithIcon("error", t('error'), error?.response?.data?.message || t('hasError'));
              }
            } else {
              openNotificationWithIcon("error", t('error'), error || t('wrongFileFormat'));
            }
          } else {
            toast.error("Import error!")
          }
        } catch (parseError) {
          openNotificationWithIcon("error", t('error'), t('wrongFileFormat'));
        }
      };

      if (rABS) reader.readAsBinaryString(values.upload[0].originFileObj);
      else reader.readAsArrayBuffer(values.upload[0].originFileObj);
    } catch (error) {
      openNotificationWithIcon("error", t('error'), error?.message || t('hasError'));
    }
  }

  const handleRetryImport = () => {
    if (currentImportJob && currentImportJob.jobId) {
      // Clear the failed job and allow user to import again
      clearJob(currentImportJob.jobId);
      setCurrentImportJob(null);
      setOpenImport(true);
    }
  };

  const handleClearJob = () => {
    if (currentImportJob && currentImportJob.jobId) {
      clearJob(currentImportJob.jobId);
      setCurrentImportJob(null);
    }
  };

  const closeModalImport = () => {
    setOpenImport(false)
    formImport.resetFields()
  }

  return (
    <div className={cx("page-shop")}>
      {contextHolder}

      {/* Import Progress Section */}
      {currentImportJob && (
        <ImportProgressCard
          job={currentImportJob}
          onRetry={handleRetryImport}
          onClear={handleClearJob}
          showDetails={true}
        />
      )}

      <ModalImport loading={loading} show={openImport} onFinish={onImport} form={formImport} onCancel={closeModalImport} />
      <ImportHistory visible={showHistory} onClose={() => setShowHistory(false)} />
      <div className={cx("title")}>
        {rolesAdd.includes(+getUser().role) && (
          <Space>
            <Button onClick={() => setOpenModal(true)} icon={<PlusOutlined />}>
              {t('addShop')}
            </Button>
            <Button
              onClick={() => setOpenImport(true)}
              icon={<CloudUploadOutlined />}
              disabled={currentImportJob && currentImportJob.status === JOB_PROCESSING}
            >
              {t('import')}
            </Button>
            <Button
              onClick={() => setShowHistory(true)}
              icon={<HistoryOutlined />}
            >
              {t('importHistory')}
            </Button>
          </Space>
        )}
        {
          rolesExport.includes(+getUser().role) &&
          <Table2XLSX dataSource={filteredListShop} />
        }

      </div>

      {/* Filter Section */}
      <div className={cx("filter-section")} style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder={t('shopCode')}
              value={filters.code}
              onChange={(e) => handleFilterChange('code', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Input
              placeholder={t('address')}
              value={filters.address}
              onChange={(e) => handleFilterChange('address', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Input
              placeholder={t('departmentManager')}
              value={filters.gl}
              onChange={(e) => handleFilterChange('gl', e.target.value)}
              allowClear
            />
          </Col>
        </Row>
      </div>

      <Table loading={loading} columns={columns} dataSource={filteredListShop} bordered />
      <ModalCreateShop
        loading={loading}
        form={formCU}
        selected={selected}
        openModal={openModal}
        onCancel={onCancel}
        dataShop={selected}
        handleSubmit={onSubmit}
        glOptions={uniqueGLOptions}
      />
    </div>
  );
}
